/**
 * Formatting for the usage meter's "Resets …" hints.
 *
 * /me/usage returns each window's reset boundary as ISO-8601 UTC (daily = next
 * 00:00 UTC, monthly = 1st of next month 00:00 UTC). These render in the
 * VIEWER'S LOCAL timezone — the daily window resets ~5 PM Pacific, so a UTC
 * label would read as "midnight" and confuse anyone west of UTC.
 *
 * Framework-free (no Angular / DOM). Kept in sync with the identical copy in
 * wg-agents-studio-ui, where it is unit-tested under vitest (usage-reset.spec).
 */

const DATE_FMT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const DATE_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

/** Short countdown to a reset, e.g. "in 6h" / "in 45m" / "shortly". Used for the
 *  daily window (always < 24h away). Empty for missing/invalid input so the
 *  template hides the line for older API responses. `nowMs` is injectable for tests. */
export function resetsIn(isoUtc: string | null | undefined, nowMs: number = Date.now()): string {
  const ms = isoUtc ? new Date(isoUtc).getTime() - nowMs : NaN;
  if (!isFinite(ms)) return '';
  if (ms <= 0) return 'shortly';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${Math.max(1, mins)}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}

/** Local calendar date of a reset, e.g. "Aug 1". Used for the monthly window. */
export function resetsOn(isoUtc: string | null | undefined): string {
  const d = isoUtc ? new Date(isoUtc) : null;
  return d && !isNaN(d.getTime()) ? DATE_FMT.format(d) : '';
}

/** Full local datetime for the hover tooltip, e.g. "Resets Fri, Aug 1, 5:00 PM". */
export function resetsAtLocal(isoUtc: string | null | undefined): string {
  const d = isoUtc ? new Date(isoUtc) : null;
  return d && !isNaN(d.getTime()) ? `Resets ${DATE_TIME_FMT.format(d)}` : '';
}
